import { useCallback, useMemo, useState } from "react";
import { ArrowUpDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";
import { PageLoader } from "@/components/ProtectedRoute";
import { AuthImage } from "@/components/AuthImage";
import { UserRow } from "@/components/admin/UserRow";
import { filterAndSortUsers, countByStatus } from "@/components/admin/userFilters";
import { useAdminUsers } from "@/hooks/useAdminUsers";

const FILTERS = ["all", "pending", "approved", "rejected"];

const SortHeader = ({ k, label, sort, onSort }) => (
  <th className="px-4 py-3 text-left">
    <button onClick={() => onSort({ key: k, dir: sort.key === k ? -sort.dir : 1 })} data-testid={`admin-sort-${k}`} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground">
      {label}<ArrowUpDown className="h-3 w-3" />
    </button>
  </th>
);

export const UsersTable = () => {
  const { t, tr } = useLang();
  const { settings } = useSettings();
  const { users, setStatus, remove } = useAdminUsers(t);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });
  const [photo, setPhoto] = useState(null);

  const packages = settings?.packages;
  const pkgName = useCallback((id) => tr(packages?.find((p) => p.id === id)?.name) || id, [packages, tr]);

  const rows = useMemo(
    () => (users ? filterAndSortUsers(users, { q, filter, sort, pkgName }) : []),
    [users, q, filter, sort, pkgName]
  );

  if (!users) return <PageLoader />;
  const Th = (p) => <SortHeader {...p} sort={sort} onSort={setSort} />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`} className={`rounded-2xl border p-4 text-left transition-colors ${filter === f ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{f === "all" ? t("total") : t(`status_${f}`)}</p>
            <p className="mt-1 text-3xl font-extrabold" data-testid={`admin-count-${f}`}>{countByStatus(users, f)}</p>
          </button>
        ))}
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} className="h-11 pl-9" data-testid="admin-users-search-input" />
      </div>
      <div className="overflow-x-auto rounded-2xl border border-border bg-card">
        <table className="w-full min-w-[960px] text-sm" data-testid="admin-users-table">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("col_photo")}</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("receipt")}</th>
              <Th k="full_name" label={t("col_name")} />
              <Th k="phone" label={t("col_phone")} />
              <Th k="country" label={t("col_country")} />
              <Th k="package_name" label={t("col_package")} />
              <Th k="status" label={t("col_status")} />
              <Th k="created_at" label={t("col_date")} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("col_actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <UserRow key={u.id} u={u} onView={(x) => setPhoto({ u: x, kind: "id" })} onReceipt={(x) => setPhoto({ u: x, kind: "receipt" })} onStatus={setStatus} onDelete={remove} />
            ))}
            {!rows.length && <tr><td colSpan={9} className="px-4 py-12 text-center text-muted-foreground" data-testid="admin-no-users">{t("no_users")}</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!photo} onOpenChange={(o) => !o && setPhoto(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-photo-dialog">
          <DialogTitle>{photo?.kind === "receipt" ? t("receipt") : t("view_photo")} — {photo?.u.full_name}</DialogTitle>
          {photo && <AuthImage path={photo.kind === "receipt" ? photo.u.payment_receipt_path : photo.u.id_photo_path} alt="" className="max-h-[70vh] w-full rounded-xl object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
